import { AccountType } from '@/common/types'
import { FC, useEffect, useState } from 'react'
import { Button, H3 } from '@/common/ui'
import { DIALOG_COPY } from '@/locales/en'
import { CircleAlertIcon } from 'lucide-react'
import { KycRequirements } from './KycRequirements'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { analytics } from '@/common/analytics/analytics'
import KycModal from './kycModal'
import { OnboardingRedirectModal } from '@/onboarding/onboardingRedirect'
import { SWIFTID_PATHS } from '@/common/constants'
import { SWIFTID_KYC_DOMAIN } from '@/common/env'
import { useInitAccountKYC } from './kycQueries'
import { KycProviders } from './types'
import { useNavigate } from 'react-router'

type Props = {
    onKycClick: () => void
    kycProviderEntityId?: string
}

export const NoKycWarning: FC<Props> = ({ onKycClick, kycProviderEntityId }) => {
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const primaryAccount = usePrimaryAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const initKyc = useInitAccountKYC()
    const navigate = useNavigate()

    const [openKyc, setOpenKyc] = useState<boolean>(false)
    const [openRedirect, setOpenRedirect] = useState<boolean>(false)
    const closeKYCModal = () => setOpenKyc(false)

    const isPrimaryAdviserUser = primaryAccount?.accountType === AccountType.ADVISER
    const noKycCopy = isPrimaryAdviserUser ? DIALOG_COPY.noKycAdviser : DIALOG_COPY.noKyc

    const accountType = account?.accountType ?? user?.accountType ?? AccountType.INDIVIDUAL
    const frankieOneOnboarding = accountType === AccountType.INDIVIDUAL

    const goToKYC = () => {
        analytics.trackWithDefaults('kyc_resumed', {
            account_type: accountType,
        })
        onKycClick()

        if (frankieOneOnboarding) {
            setOpenKyc(true)
            return
        }

        setOpenRedirect(true)
        if (kycProviderEntityId) {
            window.location.href = new URL(
                SWIFTID_PATHS.RESUME_PATH.replace('{id}', encodeURIComponent(kycProviderEntityId)),
                SWIFTID_KYC_DOMAIN,
            ).toString()
            return
        }

        if (!activeAccountId) return
        analytics.track('onboarding_kyc_initiated', { provider: KycProviders.SWIFTID, activeAccountId })
        initKyc.mutate({ accountId: activeAccountId, provider: KycProviders.SWIFTID, accountType })
    }

    useEffect(() => {
        if (!initKyc.isSuccess || !initKyc.data) return

        if (!initKyc.data.url) return
        window.location.href = initKyc.data.url
    }, [initKyc.isSuccess, initKyc.data, navigate])

    return (
        <>
            <div className="flex flex-col gap-6">
                <div className="flex flex-row items-center justify-center w-full">
                    <CircleAlertIcon className="text-destructive px-1" size={40} />
                    <H3 className="m-0 pt-1 pl-2 text-xl">{noKycCopy.title}</H3>
                </div>
                <p style={{ whiteSpace: 'pre-line' }} className="py-1">
                    {noKycCopy.description}
                </p>
                <KycRequirements
                    frankieOneOnboarding={frankieOneOnboarding}
                    accountType={account?.accountType ?? user?.accountType ?? AccountType.INDIVIDUAL}
                />
                <div className={`flex w-full items-center justify-end`}>
                    <Button
                        className="w-full sm:w-auto h-[60px] sm:min-w-[130px] mt-5"
                        variant="secondary"
                        fullWidth
                        innerClassName="font-medium"
                        onClick={goToKYC}
                    >
                        {noKycCopy.primaryButton}
                    </Button>
                </div>
            </div>
            <OnboardingRedirectModal open={openRedirect} />
            <KycModal open={openKyc} onClose={closeKYCModal} />
        </>
    )
}
